import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { Rock, RockCategory, User } from '../../types'
import EmptyState from '../shared/EmptyState'
import RockTable from '../rocks/RockTable'
import RockPlanner from '../rocks/RockPlanner'
import RockCheckInModal from '../rocks/RockCheckInModal'
import AddTopicModal, { type TopicCreatePayload, type TopicSubmitHandlers } from './TopicModal'
import { queueMeetingTopicCreate } from '../../utils/rockSaveQueue'

interface Props {
  rocks: Rock[]
  meetingId: number
  teamId: number
  teamMembers?: User[]
  rockCategories?: RockCategory[]
  canCreateRock?: boolean
  currentUserId?: number
  quarter_label?: string
  quarter_end_date?: string
  /**
   * Task #798: offers each rock row's actions menu ("Create Topic"). The
   * meeting page computes it exactly as it does for the Scoreboard — a
   * facilitator on an in-progress meeting — so it isn't recomputed here.
   */
  canCreateTopic?: boolean
}

export default function SectionRockReview({
  rocks,
  meetingId,
  teamId,
  teamMembers,
  rockCategories,
  canCreateRock,
  currentUserId,
  quarter_label,
  quarter_end_date,
  canCreateTopic,
}: Props) {
  const [plannerRock, setPlannerRock] = useState<Rock | null>(null)
  const [checkInRock, setCheckInRock] = useState<Rock | null>(null)
  // Task #614: "+ Add Rock" opens the F.A.S.T. Rock Planner in create mode;
  // its first autosave creates the rock and hands back the id so the same
  // open panel can bind to the real record.
  const [creatingRock, setCreatingRock] = useState(false)
  const [createdRockId, setCreatedRockId] = useState<number | null>(null)
  const [groupBy, setGroupBy] = useState<'owner' | 'none'>('none')
  // Task #798: the rock whose row opened the "Add to The List" pop-up; null
  // while the pop-up is closed.
  const [topicModalRock, setTopicModalRock] = useState<Rock | null>(null)
  // Inline confirmation/error for the row menu's Create Topic action: the
  // shared flash banner sits at the top of the page, off-screen while the
  // team is working down the rock list.
  const [topicStatus, setTopicStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const topicStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (topicStatusTimer.current) clearTimeout(topicStatusTimer.current)
  }, [])

  const showTopicStatus = useCallback((kind: 'success' | 'error', text: string) => {
    if (topicStatusTimer.current) clearTimeout(topicStatusTimer.current)
    setTopicStatus({ kind, text })
    topicStatusTimer.current = setTimeout(() => setTopicStatus(null), 6000)
  }, [])

  // The pop-up's create rides the same serialized queue as every rock edit —
  // an independent Inertia visit would cancel a percent/due-date/planner save
  // still in flight (and vice versa). Nothing is written until submit.
  const submitTopic = useCallback((
    payload: TopicCreatePayload,
    { onSuccess, onError }: TopicSubmitHandlers,
  ) => {
    const title = String(payload.topic.title ?? '')
    queueMeetingTopicCreate(meetingId, payload as unknown as Record<string, unknown>, {
      onSuccess: () => {
        showTopicStatus('success', `“${title}” added to The List.`)
        onSuccess()
      },
      onError: (_errors, message) => {
        showTopicStatus(
          'error',
          message
            ? `Could not add “${title}” to The List: ${message}`
            : `Could not add “${title}” to The List. Please try again.`,
        )
        onError(message)
      },
    })
  }, [meetingId, showTopicStatus])

  // Owner pre-fill: the rock's owner, but only when that person is actually
  // selectable on this team's member list. Otherwise leave it unset rather
  // than guessing. The owner is already serialized with each meeting rock.
  const topicModalOwnerId = useMemo<number | ''>(() => {
    const ownerId = topicModalRock?.owner?.id ?? topicModalRock?.owner_id
    if (!ownerId) return ''
    return (teamMembers || []).some(m => m.id === ownerId) ? ownerId : ''
  }, [topicModalRock, teamMembers])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase">
            Rock Review
          </h3>
          {quarter_label && quarter_end_date && (
            <p className="text-sm font-semibold text-text-muted uppercase tracking-wider">
              {quarter_label} — Ends {quarter_end_date}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'owner' | 'none')}
            aria-label="Group rocks"
            className="px-3 py-1.5 border border-stone/30 rounded-lg text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
          >
            <option value="none">No Grouping</option>
            <option value="owner">Group by Owner</option>
          </select>
          {canCreateRock && teamId && (
            <button
              onClick={() => { setCreatedRockId(null); setCreatingRock(true) }}
              className="px-3 py-1.5 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 font-heading"
            >
              + Add Rock
            </button>
          )}
          {teamId && (
            <Link
              href={`/teams/${teamId}/rocks`}
              className="text-sm text-pinnacle-red hover:text-pinnacle-red/80 font-medium"
            >
              View All Rocks
            </Link>
          )}
        </div>
      </div>

      {rocks.length === 0 ? (
        <EmptyState
          title="No Rocks Yet"
          description="Add quarterly rocks to track your team's key priorities."
        />
      ) : (
        // The same table the F.A.S.T. Rocks page renders, so every column and
        // every inline edit behaves identically in the meeting. Only the
        // reorder destination differs: the meeting keeps its own rock order.
        <RockTable
          rocks={rocks}
          teamId={teamId}
          groupBy={groupBy}
          teamMembers={teamMembers || []}
          categories={rockCategories || []}
          onViewDetail={setPlannerRock}
          canReorder={!!canCreateRock}
          reorderUrl={`/meetings/${meetingId}/rocks/reorder`}
          meetingId={meetingId}
          showCheckInNote
          showRowMenu={canCreateTopic === true}
          onCreateTopic={setTopicModalRock}
        />
      )}

      {topicStatus && (
        <p
          role={topicStatus.kind === 'error' ? 'alert' : 'status'}
          className={`px-3 py-2 rounded-lg text-sm ${
            topicStatus.kind === 'success'
              ? 'bg-[#2E7D32]/10 text-[#2E7D32]'
              : 'bg-[#ED1C24]/10 text-[#ED1C24]'
          }`}
        >
          {topicStatus.text}
        </p>
      )}

      {/* Mounted whenever the row menu is offered so it keeps what the user
          entered across the meeting's 10s heartbeat prop refreshes. */}
      {canCreateTopic && (
        <AddTopicModal
          isOpen={topicModalRock !== null}
          onClose={() => setTopicModalRock(null)}
          meetingId={meetingId}
          teamMembers={teamMembers || []}
          currentUserId={currentUserId}
          initialTitle={topicModalRock?.title ?? ''}
          initialOwnerId={topicModalOwnerId}
          source={topicModalRock ? { type: 'Rock', id: topicModalRock.id } : null}
          onSubmit={submitTopic}
        />
      )}

      {plannerRock && teamMembers && (() => {
        const idx = rocks.findIndex(r => r.id === plannerRock.id)
        return (
          <RockPlanner
            key={plannerRock.id}
            rock={rocks.find(r => r.id === plannerRock.id) || plannerRock}
            teamId={teamId}
            teamMembers={teamMembers}
            categories={rockCategories || []}
            onClose={() => setPlannerRock(null)}
            onCheckIn={(r) => { setPlannerRock(null); setCheckInRock(r) }}
            onPrevRock={idx > 0 ? () => setPlannerRock(rocks[idx - 1]) : undefined}
            onNextRock={idx >= 0 && idx < rocks.length - 1 ? () => setPlannerRock(rocks[idx + 1]) : undefined}
          />
        )
      })()}

      {checkInRock && (() => {
        const idx = rocks.findIndex(r => r.id === checkInRock.id)
        return (
          <RockCheckInModal
            rock={rocks.find(r => r.id === checkInRock.id) || checkInRock}
            teamId={teamId}
            meetingId={meetingId}
            onClose={() => setCheckInRock(null)}
            onPrevRock={idx > 0 ? () => setCheckInRock(rocks[idx - 1]) : undefined}
            onNextRock={idx >= 0 && idx < rocks.length - 1 ? () => setCheckInRock(rocks[idx + 1]) : undefined}
            onSaved={() => {
              if (idx >= 0 && idx < rocks.length - 1) {
                setCheckInRock(rocks[idx + 1])
              } else {
                setCheckInRock(null)
              }
            }}
          />
        )
      })()}

      {creatingRock && teamMembers && (
        <RockPlanner
          key="rock-planner-create"
          rock={createdRockId ? rocks.find(r => r.id === createdRockId) || null : null}
          teamId={teamId}
          teamMembers={teamMembers}
          categories={rockCategories || []}
          currentUserId={currentUserId}
          onCreated={setCreatedRockId}
          onClose={() => { setCreatingRock(false); setCreatedRockId(null) }}
          onCheckIn={(r) => { setCreatingRock(false); setCreatedRockId(null); setCheckInRock(r) }}
        />
      )}
    </div>
  )
}
